// Utility functions for consistent chip styling across the application
// This ensures all chips follow the design system

export const getChipVariant = (type, theme, options = {}) => {
  const filled = options.filled === true;
  const variants = {
    // Status-based variants
    active: {
      variant: 'outlined',
      style: {
        backgroundColor: theme.palette.utilities.active.light,
        color: theme.palette.utilities.active.contrastText,
        border: `1px solid ${theme.palette.utilities.active.main}`,
        fontWeight: 500,
        borderRadius: '16px',
        fontSize: '0.875rem'
      }
    },
    inactive: {
      variant: 'outlined',
      style: {
        backgroundColor: theme.palette.utilities.inactive.light,
        color: theme.palette.utilities.inactive.contrastText,
        border: `1px solid ${theme.palette.utilities.inactive.main}`,
        fontWeight: 500,
        borderRadius: '16px',
        fontSize: '0.875rem'
      }
    },
    tenant: {
      variant: 'outlined',
      style: {
        backgroundColor: theme.palette.utilities.tenant.light,
        color: theme.palette.utilities.tenant.contrastText,
        border: `1px solid ${theme.palette.utilities.tenant.main}`,
        fontWeight: 500,
        borderRadius: '16px',
        fontSize: '0.875rem'
      }
    },
    // Semantic variants
    success: {
      variant: filled ? 'filled' : 'outlined',
      style: filled
        ? { backgroundColor: theme.palette.success.main, color: '#ffffff', fontWeight: 500, borderRadius: '16px', fontSize: '0.875rem' }
        : { backgroundColor: `rgba(${hexToRgb(theme.palette.success.main)}, 0.1)`, color: theme.palette.success.main, border: `1px solid ${theme.palette.success.main}`, fontWeight: 500, borderRadius: '16px', fontSize: '0.875rem' }
    },
    warning: {
      variant: filled ? 'filled' : 'outlined',
      style: filled
        ? { backgroundColor: theme.palette.warning.main, color: '#ffffff', fontWeight: 500, borderRadius: '16px', fontSize: '0.875rem' }
        : { backgroundColor: `rgba(${hexToRgb(theme.palette.warning.main)}, 0.1)`, color: theme.palette.warning.main, border: `1px solid ${theme.palette.warning.main}`, fontWeight: 500, borderRadius: '16px', fontSize: '0.875rem' }
    },
    error: {
      variant: filled ? 'filled' : 'outlined',
      style: filled
        ? { backgroundColor: theme.palette.error.main, color: '#ffffff', fontWeight: 500, borderRadius: '16px', fontSize: '0.875rem' }
        : { backgroundColor: `rgba(${hexToRgb(theme.palette.error.main)}, 0.1)`, color: theme.palette.error.main, border: `1px solid ${theme.palette.error.main}`, fontWeight: 500, borderRadius: '16px', fontSize: '0.875rem' }
    },
    alert: {
      variant: filled ? 'filled' : 'outlined',
      style: filled
        ? { backgroundColor: theme.palette.utilities?.alert?.main || theme.palette.error.main, color: '#ffffff', fontWeight: 500, borderRadius: '16px', fontSize: '0.875rem' }
        : { backgroundColor: theme.palette.utilities.alert.light, color: theme.palette.utilities.alert.contrastText, border: `1px solid ${theme.palette.utilities.alert.main}`, fontWeight: 500, borderRadius: '16px', fontSize: '0.875rem' }
    },
    info: {
      variant: filled ? 'filled' : 'outlined',
      style: filled
        ? { backgroundColor: theme.palette.info.main, color: '#ffffff', fontWeight: 500, borderRadius: '16px', fontSize: '0.875rem' }
        : { backgroundColor: `rgba(${hexToRgb(theme.palette.info.main)}, 0.1)`, color: theme.palette.info.main, border: `1px solid ${theme.palette.info.main}`, fontWeight: 500, borderRadius: '16px', fontSize: '0.875rem' }
    },
    // Default/neutral variant
    default: {
      variant: filled ? 'filled' : 'outlined',
      style: filled
        ? { backgroundColor: theme.palette.grey[500], color: '#ffffff', fontWeight: 500, borderRadius: '16px', fontSize: '0.875rem' }
        : { backgroundColor: theme.palette.utilities.neutral.light, color: theme.palette.utilities.neutral.contrastText, border: `1px solid ${theme.palette.grey[400]}`, fontWeight: 500, borderRadius: '16px', fontSize: '0.875rem' }
    },
    // Primary and secondary variants
    primary: {
      variant: 'filled',
      style: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontWeight: 500,
        borderRadius: '16px',
        fontSize: '0.875rem'
      }
    },
    secondary: {
      variant: 'filled',
      style: {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        fontWeight: 500,
        borderRadius: '16px',
        fontSize: '0.875rem'
      }
    }
  };

  return variants[type?.toLowerCase()] || variants.default;
};

// Helper function to convert hex color to RGB values
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? 
    `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : 
    '0, 0, 0';
};

// Legacy color mapping for backward compatibility
export const mapLegacyColors = (color) => {
  const colorMap = {
    '#f44336': 'error',
    '#ff9800': 'warning', 
    '#4caf50': 'success',
    '#2196f3': 'info',
    '#757575': 'default',
    'green': 'success',
    'red': 'error',
    'orange': 'warning',
    'blue': 'info'
  };
  
  return colorMap[color?.toLowerCase()] || 'default';
};
